import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../components/Logo";
import { usePageMeta } from "../../components/Layout";

export default function AdminLogin() {
    usePageMeta({ title: "Sign in | Studio G4 Admin", robots: "noindex, nofollow, noarchive, nosnippet" });
    const { user, isLoading, refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        if (!isLoading && user) {
            const to = location.state?.from || "/admin";
            navigate(to, { replace: true });
        }
    }, [isLoading, user, navigate, location]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await api.post("/auth/login", { email: email.trim(), password });
            await refresh();
            navigate("/admin", { replace: true });
        } catch (ex) {
            if (ex?.response?.status === 429) setErr("Too many attempts. Please wait a moment and try again.");
            else setErr("Incorrect email or password.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-offwhite flex items-center justify-center px-6" data-testid="admin-login-page">
            <form onSubmit={submit} className="w-full max-w-[400px] bg-white/70 border border-charcoal/10 rounded-md p-8 shadow-sm">
                <div className="flex flex-col items-center mb-8">
                    <Logo variant="login" />
                    <div className="mt-3 font-manrope text-[10px] uppercase tracking-[0.24em] text-charcoal/50">Admin</div>
                </div>
                <h1 className="font-manrope font-bold text-charcoal text-[22px] mb-6 text-center">Sign in</h1>

                <label className="block font-manrope font-semibold text-charcoal text-[13px] mb-2">Email</label>
                <input
                    type="email"
                    required
                    autoFocus
                    autoComplete="username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full mb-5 px-4 py-2.5 bg-offwhite border border-charcoal/20 rounded-md font-inter text-[15px] text-charcoal focus:border-terracotta focus:outline-none"
                    data-testid="login-email"
                />
                <label className="block font-manrope font-semibold text-charcoal text-[13px] mb-2">Password</label>
                <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full mb-6 px-4 py-2.5 bg-offwhite border border-charcoal/20 rounded-md font-inter text-[15px] text-charcoal focus:border-terracotta focus:outline-none"
                    data-testid="login-password"
                />
                <button
                    type="submit"
                    disabled={busy}
                    className="w-full font-manrope font-semibold text-[15px] py-3 rounded-md bg-terracotta text-offwhite hover:bg-terracotta-600 disabled:opacity-70 transition-colors"
                    data-testid="login-submit"
                >
                    {busy ? "Signing in…" : "Sign in"}
                </button>
                {err && <p className="mt-4 text-terracotta text-[14px] font-inter text-center" data-testid="login-error">{err}</p>}
            </form>
        </div>
    );
}
